import React from 'react';
import { useTranslation } from 'react-i18next';
import { ClientInfo } from '../../../../types/clientOnboarding';

interface Props {
  data: Partial<ClientInfo>;
  onChange: (data: Partial<ClientInfo>) => void;
  errors: Record<string, string>;
}

const inputCls =
  'w-full p-2.5 border border-[var(--border-light)] rounded-lg bg-[var(--surface)] text-[var(--text-primary)] focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent';
const labelCls = 'block text-sm font-medium text-[var(--text-secondary)] mb-1';
const errorCls = 'text-[var(--color-danger)] text-xs mt-1';

const ClientStep: React.FC<Props> = ({ data, onChange, errors }) => {
  const { t } = useTranslation(['admin_client_onboarding']);

  const set = (key: keyof ClientInfo, value: string) =>
    onChange({ ...data, [key]: value });

  return (
    <div className="space-y-4" data-testid="onboarding-step-client">
      <div>
        <h3 className="text-heading-3 text-[var(--text-primary)] mb-1">{t('step1.heading')}</h3>
        <p className="text-sm text-[var(--text-secondary)]">{t('step1.description')}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={labelCls} htmlFor="client-display-name">{t('step1.display_name')} *</label>
          {/* R2R-P2-04: Render any display_name error — missing (client.display_name)
              or over-length (client.display_name_max) — through a single inline path. */}
          {(() => {
            const nameError = errors['client.display_name'] || errors['client.display_name_max'];
            return (
              <>
                <input id="client-display-name" className={inputCls} value={data.display_name || ''}
                  onChange={e => set('display_name', e.target.value)}
                  placeholder={t('step1.display_name_ph')} data-testid="onboarding-display-name"
                  aria-invalid={nameError ? true : undefined}
                  aria-describedby={nameError ? 'client-display-name-err' : undefined} />
                {nameError && <p id="client-display-name-err" className={errorCls} role="alert">{nameError}</p>}
              </>
            );
          })()}
        </div>
        <div>
          <label className={labelCls} htmlFor="client-legal-name">{t('step1.legal_name')} *</label>
          {(() => {
            const nameError = errors['client.legal_name'] || errors['client.legal_name_max'];
            return (
              <>
                <input id="client-legal-name" className={inputCls} value={data.legal_name || ''}
                  onChange={e => set('legal_name', e.target.value)}
                  placeholder={t('step1.legal_name_ph')} data-testid="onboarding-legal-name"
                  aria-invalid={nameError ? true : undefined}
                  aria-describedby={nameError ? 'client-legal-name-err' : undefined} />
                {nameError && <p id="client-legal-name-err" className={errorCls} role="alert">{nameError}</p>}
              </>
            );
          })()}
        </div>
        <div>
          <label className={labelCls} htmlFor="client-country">{t('step1.country')} *</label>
          <input id="client-country" className={inputCls} value={data.country || ''}
            onChange={e => set('country', e.target.value)}
            placeholder={t('step1.country_ph')} data-testid="onboarding-country"
            aria-describedby={errors['client.country'] ? 'client-country-err' : undefined} />
          {errors['client.country'] && <p id="client-country-err" className={errorCls} role="alert">{errors['client.country']}</p>}
        </div>
        <div>
          <label className={labelCls} htmlFor="client-language">{t('step1.language')}</label>
          <input id="client-language" className={inputCls} value={data.language ?? ''}
            onChange={e => set('language', e.target.value)} data-testid="onboarding-language"
            placeholder="fr" />
        </div>
        <div>
          <label className={labelCls} htmlFor="client-timezone">{t('step1.timezone')}</label>
          <input id="client-timezone" className={inputCls} value={data.timezone ?? ''}
            onChange={e => set('timezone', e.target.value)} data-testid="onboarding-timezone"
            placeholder="Europe/Paris" />
        </div>
        <div>
          <label className={labelCls} htmlFor="client-currency">{t('step1.currency')}</label>
          <input id="client-currency" className={inputCls} value={data.currency ?? ''}
            onChange={e => set('currency', e.target.value)} data-testid="onboarding-currency"
            placeholder="EUR" />
        </div>
        <div>
          <label className={labelCls} htmlFor="client-email">{t('step1.email')} *</label>
          {/* R2R-P2-02: Render any email error — invalid format (client.email)
              or over-length (client.email_max) — through a single inline path. */}
          {(() => {
            const emailError = errors['client.email'] || errors['client.email_max'];
            return (
              <>
                <input id="client-email" className={inputCls} type="email" value={data.email || ''}
                  onChange={e => set('email', e.target.value)}
                  placeholder={t('step1.email_ph')} data-testid="onboarding-email"
                  aria-invalid={emailError ? true : undefined}
                  aria-describedby={emailError ? 'client-email-err' : undefined} />
                {emailError && <p id="client-email-err" className={errorCls} role="alert">{emailError}</p>}
              </>
            );
          })()}
        </div>
        <div>
          <label className={labelCls} htmlFor="client-phone">{t('step1.phone')}</label>
          <input id="client-phone" className={inputCls} type="tel" value={data.phone || ''}
            onChange={e => set('phone', e.target.value)}
            placeholder={t('step1.phone_ph')} data-testid="onboarding-phone" />
        </div>
      </div>
      <div>
        <label className={labelCls} htmlFor="client-address">{t('step1.address')}</label>
        <textarea id="client-address" className={inputCls} rows={2} value={data.address || ''}
          onChange={e => set('address', e.target.value)}
          placeholder={t('step1.address_ph')} data-testid="onboarding-address" />
      </div>
      <div className="p-3 rounded-lg bg-[var(--color-info)]/10 border border-[var(--color-info)]/30 text-sm text-[var(--text-secondary)]">
        {t('step1.organizationIdPlanHint')}
      </div>
    </div>
  );
};

export default ClientStep;
